// Main wizard component that will replace the existing form when feature flag is enabled

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KeepScreenOnBadge } from '@/components/ui/KeepScreenOnBadge';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Gender, StoryPage } from '@/utils/ai-types';
import { getDefaultLanguage } from '@/utils/languages';
import { initialLanguageFromParam } from '@/lib/languages/initial-language';
import { shouldResetCharacters, mergeFaceIdentity } from '@/lib/wizard/faces';
import { storyInputsKey } from '@/lib/wizard/story-inputs-key';
import { useWakeLock } from '@/utils/wakeLock';
import { WizardErrorBoundary } from './components/WizardErrorBoundary';
import { WizardContainer } from './components/WizardContainer';
import { StepIndicator } from './components/StepIndicator';
import { buildMultiStepSequence, nextStepAfter } from './step-sequence';
import type { SeedOutcome } from '@/lib/free-cover/continuation';
import { coverStorySeed, canReuseCover, seedPhotoIsStale, shouldApplyContinuation, classifySeedResponse, afterSeedRefresh } from '@/lib/free-cover/continuation';
import { AdjustLooksDrawer } from './components/AdjustLooksDrawer';
import { isAppearancePrefillConfident, hasAllRequiredTraits } from '@/lib/wizard/appearance';
import { pageTextLimit, pageTextLength, type ReadingLevel } from '@/lib/story/reading-level';
import { WizardNavigation, MobileWizardNavigation } from './components/WizardNavigation';
import { WizardStep } from './components/WizardStep';
import { useWizardState } from './hooks/useWizardState';
import { useStepValidation } from './hooks/useStepValidation';
import { wizardLogger } from './utils/logger';
import { WIZARD_DEFAULTS } from './constants';
import { WizardStep as WizardStepType } from './types';
import StoryTypeStepSimple from './components/steps/StoryTypeStepSimple';
import DebugStoryTypeStep from './components/steps/DebugStoryTypeStep';
import { PhotoUploadStep } from './components/steps/PhotoUploadStep';
import { PhotoOnlyUploadStep } from './components/steps/PhotoOnlyUploadStep';
import { CharacterDetailsReviewStep } from './components/steps/CharacterDetailsReviewStep';
import { CharacterDetailsStep } from './components/steps/CharacterDetailsStep';
import { MultiCharacterDetailsStep } from './components/steps/MultiCharacterDetailsStep';
import { AppearanceStep } from './components/steps/AppearanceStep';
import AppearanceReviewStep from './components/steps/AppearanceReviewStep';
import MinimalAppearanceReviewStep from './components/steps/MinimalAppearanceReviewStep';
import StorySelectionStep from './components/steps/StorySelectionStep';
import MultiCharacterStorySelectionStep from './components/steps/MultiCharacterStorySelectionStep';
import { MultiCharacterAppearanceStep } from './components/steps/MultiCharacterAppearanceStep';
import { StyleSelectionStep, Style } from './components/steps/StyleSelectionStep';
import { StoryCreationStep } from './components/steps/StoryCreationStep';
import { StoryPreviewStep } from './components/steps/StoryPreviewStep';
import { fetchWithToast } from '@/utils/fetchWithToast';
import { getOrCreateSessionId } from '@/utils/session-manager';
import { LoadingOverlay } from './components/LoadingOverlay';
import { GenerationProgressTracker } from './components/GenerationProgressTracker';
import { CoverPreviewOverlay } from './components/CoverPreviewOverlay';
import { enableWizardDebug } from './utils/debug';
import { initializeGlobalDebugState } from './utils/globalDebug';
import { personalizeTitle, filterCommentsForCharacterCount } from '@/lib/unified-story-templates';
import CoverNotifyCard from '@/components/CoverNotifyCard';
import { track } from '@/utils/analytics';
import { WIZARD_COVER_PREVIEW_TIME, STORY_TEXT_TIME } from '@/lib/seo/site-facts';
import { useWizardVariant } from './hooks/useWizardVariant';
import { DEDICATION_MAX_CHARS, sanitizeDedication, dedicationFitsBook } from '@/lib/books/dedication';
import { photoConsentJson } from '@/components/consent/photo-consent-gate';
import { copy } from '@/lib/ethics/copy';
import { wizardPhotoConsent } from './photo-consent';


// Form schema - same as original but will be built progressively
const formSchema = z.object({
  storyType: z.enum(['single', 'multi']).optional(),
  language: z.string().min(2).max(10),
  readingLevel: z.enum(['picture', 'story']).default('picture'),
  // Single character fields
  childFirstName: z.string().min(2).max(50).optional(),
  childAge: z.string().optional(),
  childGender: z.nativeEnum(Gender).optional(),
  hairColor: z.string().optional(),
  skinColor: z.string().optional(),
  eyeColor: z.string().optional(),
  hairTexture: z.string().optional(),
  // Multi-character fields
  characterCount: z.number().min(1).max(5).optional(),
  mainCharacterIndex: z.number().optional(),
  relationshipType: z.string().optional(),
  characters: z.array(z.object({
    name: z.string().min(2).max(50),
    age: z.string(),
    gender: z.string(),
    skinTone: z.string(),
    hairColor: z.string(),
    eyeColor: z.string(),
    hairTexture: z.string(),
    heightInches: z.string().optional(),
    heightUserSet: z.boolean().optional(),
    bbox: z.object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number()
    }).optional(),
    thumbnail: z.string().optional(),
    aiConfidence: z.number().optional(),
    aiFieldConfidence: z.record(z.number()).optional(),
    aiNotes: z.string().optional()
  })).optional(),
  detectedFaces: z.array(z.any()).optional(),
  // Common fields
  bookTitle: z.string().min(3).max(100),
  additionalComments: z.string().min(5).max(2000),
  dedication: z.string().max(DEDICATION_MAX_CHARS * 2).optional(),
  styleId: z.string().min(1),
  pageCount: z.number().min(8).max(24),
  generateFullBook: z.boolean().optional(),
  storyContext: z.object({
    culturalSetting: z.string().optional(),
    culturalDetails: z.string().optional(),
  }).optional(),
  characterPhoto: z.string().optional(), // Photo URL
});

type FormData = z.infer<typeof formSchema>;

// Initial styles data (same as in original form)
const initialStyles: Style[] = [{
  id: 'cartoon',
  name: 'Cartoon',
  description: 'Bright and colorful cartoon illustrations',
  previewImageUrl: '/styles/cartoon.png',
  isActive: true,
}, {
  id: 'watercolor',
  name: 'Watercolor',
  description: 'Soft and dreamy watercolor paintings',
  previewImageUrl: '/styles/watercolor.png',
  isActive: true,
}, {
  id: 'realistic',
  name: 'Realistic',
  description: 'Detailed and lifelike illustrations',
  previewImageUrl: '/styles/realistic.png',
  isActive: true,
}, {
  id: 'graphicnovel',
  name: 'Graphic Novel',
  description: 'Bold and dramatic comic book style illustrations',
  previewImageUrl: '/styles/graphicnovel.png',
  isActive: true,
}, {
  id: 'enchantedwatercolor',
  name: 'Enchanted Watercolor',
  description: 'Whimsical, detailed, and heartwarming hand-painted illustrations',
  previewImageUrl: '/styles/enchantedwatercolor.png',
  isActive: true,
}, {
  id: 'modern3d',
  name: 'Modern 3D Animation',
  description: 'Contemporary computer-animated illustrations with vibrant colors',
  previewImageUrl: '/styles/modern3d.png',
  isActive: true,
}, {
  id: 'anime',
  name: 'Anime',
  description: 'Japanese animation style with expressive characters',
  previewImageUrl: '/styles/anime.png',
  isActive: true,
  experimental: true
}, {
  id: 'manga',
  name: 'Manga',
  description: 'Japanese comic book style with dramatic angles',
  previewImageUrl: '/styles/manga.png',
  isActive: true,
  experimental: true
}, {
  id: 'sketch',
  name: 'Sketch',
  description: 'Hand-drawn pencil sketch with artistic shading',
  previewImageUrl: '/styles/sketch.png',
  isActive: true,
  experimental: true
}, {
  id: 'oil-painting',
  name: 'Oil Painting',
  description: 'Classic oil painting with rich textures',
  previewImageUrl: '/styles/oil-painting.png',
  isActive: true,
  experimental: true
}, {
  id: 'pixel-art',
  name: 'Pixel Art',
  description: 'Retro 8-bit and 16-bit video game style',
  previewImageUrl: '/styles/pixel-art.png',
  isActive: true,
  experimental: true
}, {
  id: 'storybook',
  name: 'Classic Storybook',
  description: 'Traditional illustrated children\'s book style',
  previewImageUrl: '/styles/storybook.png',
  isActive: true,
  experimental: true
}, {
  id: 'pop-art',
  name: 'Pop Art',
  description: 'Bold, colorful pop art with graphic elements',
  previewImageUrl: '/styles/pop-art.png',
  isActive: true,
  experimental: true
}, {
  id: 'impressionist',
  name: 'Impressionist',
  description: 'Soft brushstrokes and dreamy impressionist style',
  previewImageUrl: '/styles/impressionist.png',
  isActive: true,
  experimental: true
}, {
  id: 'caricature',
  name: 'Caricature',
  description: 'Fun exaggerated features with playful proportions',
  previewImageUrl: '/styles/caricature.png',
  isActive: true,
  experimental: true
}];

export interface CreateBookWizardProps {
  // Props will be added as we build out the wizard
}

export function CreateBookWizard(props: CreateBookWizardProps) {
  // Minimal logging - only log on first render in development
  const hasLoggedRef = useRef(false);
  
  // Debug: Track component instance and render count
  const componentInstanceId = useRef(`wizard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const renderCount = useRef(0);
  renderCount.current++;
  
  // Disabled for performance - only log once if needed
  // const trackHook = (hookName: string) => {
  //   // Disabled for performance
  // };
  
  // Define all hooks BEFORE useEffect that uses them (no tracking for performance)
  const router = useRouter();
  const searchParams = useSearchParams();
  const { toast } = useToast();
  const { requestWakeLock, releaseWakeLock } = useWakeLock();
  // FAST_PATH_WIZARD A/B arm ('control' until assigned on mount; see lib/wizard/fast-path.ts)
  const { variant, ready: variantReady } = useWizardVariant(searchParams?.get('wizard'));

  // Capture partner code from URL and store in localStorage for checkout
  useEffect(() => {
    const partnerCode = searchParams?.get('partner');
    if (partnerCode) {
      localStorage.setItem('partnerCode', partnerCode);
      console.log(`🤝 Partner code captured: ${partnerCode}`);
    }
  }, [searchParams]);

  // Funnel: one wizard_started per mount; source distinguishes free-cover and language-page arrivals from direct entry.
  useEffect(() => {
    const source = searchParams?.get('fromCover') ? 'free_cover' : searchParams?.get('language') ? 'language_page' : 'direct';
    track('wizard_started', { source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Enable debug utilities - now hooks are available (deferred for performance)
  useEffect(() => {
    // Only enable debug in development after a delay to avoid blocking UI
    if (process.env.NODE_ENV === 'development') {
      const timer = setTimeout(() => {
        enableWizardDebug();
        initializeGlobalDebugState();
      }, 1000); // Increased delay
      
      return () => clearTimeout(timer);
    }
    
    // Request wake lock when wizard starts to prevent screen from sleeping
    requestWakeLock('create-book-wizard').catch(() => {
      // Silently fail
    });
    
    return () => {
      // Release wake lock when wizard unmounts
      releaseWakeLock().catch(() => {
        // Silently fail
      });
    };
  }, []); // Remove dependencies to prevent re-running
  
  // Story generation state (no tracking for performance)
  const [storyPreview, setStoryPreview] = useState<StoryPage[] | null>(null);
  const [, setDedicationTick] = useState(0); // form.getValues() doesn't subscribe; bump to re-render on dedication edits
  const [coverData, setCoverData] = useState<any>(null); // NEW: Track cover data
  const [isLoading, setIsLoading] = useState(false);
  const [styles] = useState<Style[]>(initialStyles);
  const [characterCount, setCharacterCount] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [characterOutfits, setCharacterOutfits] = useState<Record<string, string> | null>(null);
  const [photoFeatures, setPhotoFeatures] = useState<Record<string, any> | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentOperation, setCurrentOperation] = useState<'story' | 'preview' | 'cover' | null>(null);
  const [storyRequestId, setStoryRequestId] = useState<string | null>(null);
  const [previewRequestId, setPreviewRequestId] = useState<string | null>(null);
  const [currentBookId, setCurrentBookId] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null); // Use ref for direct control
  const isPollingRef = useRef(false); // Track if we're currently polling
  const [isReducingText, setIsReducingText] = useState(false); // Track text reduction in progress

  // Cover-notify escape hatch
  const COVER_NOTIFY_DELAY_MS = 15000;
  const [showCoverNotify, setShowCoverNotify] = useState(false);
  const [coverNotifyDismissed, setCoverNotifyDismissed] = useState(false);
  const coverNotifyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper function to validate and reduce page text that exceeds the reading level's limit
  const validateAndReducePageText = async (pages: StoryPage[], readingLevel: ReadingLevel): Promise<StoryPage[]> => {
    const pageLanguage = form.getValues('language') || 'en';
    const limit = pageTextLimit(readingLevel, pageLanguage);
    // Find pages that exceed the word limit (skip cover page - pageNumber 0)
    const pagesNeedingReduction = pages
      .filter(page => {
        const pageNum = page.pageNumber ?? (page as any).page ?? 0;
        if (pageNum === 0) return false; // Skip cover page
        return pageTextLength(page.content || '', pageLanguage) > limit.max;
      })
      .map(page => ({
        pageNumber: page.pageNumber ?? (page as any).page ?? 0,
        text: page.content || '',
        wordCount: pageTextLength(page.content || '', pageLanguage)
      }));

    if (pagesNeedingReduction.length === 0) {
      console.log('[WIZARD] All pages within word limit, no reduction needed');
      return pages;
    }

    console.log(`[WIZARD] Found ${pagesNeedingReduction.length} pages exceeding ${limit.max} ${limit.unit}:`,
      pagesNeedingReduction.map(p => `Page ${p.pageNumber}: ${p.wordCount} ${limit.unit}`)
    );

    setIsReducingText(true);

    try {
      const language = form.getValues('language') || 'en';
      const bookTitle = form.getValues('bookTitle') || '';

      const response = await fetch('/api/books/reduce-page-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pages: pagesNeedingReduction,
          language,
          bookTitle,
          readingLevel
        })
      });

      if (!response.ok) {
        console.error('[WIZARD] Failed to reduce page text:', response.status);
        return pages; // Return original pages if reduction fails
      }

      const result = await response.json();

      if (!result.success || !result.reducedPages) {
        console.error('[WIZARD] Text reduction failed:', result.error);
        return pages; // Return original pages if reduction fails
      }

      // Apply reduced text to the original pages
      const updatedPages = pages.map(page => {
        const pageNum = page.pageNumber ?? (page as any).page ?? 0;
        const reducedPage = result.reducedPages.find((rp: any) => rp.pageNumber === pageNum);

        if (reducedPage) {
          console.log(`[WIZARD] Applied reduced text to page ${pageNum}: ${reducedPage.originalWordCount} -> ${reducedPage.newWordCount} words`);
          return {
            ...page,
            content: reducedPage.reducedText
          };
        }
        return page;
      });

      toast({
        title: "Text optimized",
        description: `${pagesNeedingReduction.length} page(s) adjusted for better readability`
      });

      return updatedPages;
    } catch (error) {
      console.error('[WIZARD] Error reducing page text:', error);
      return pages; // Return original pages on error
    } finally {
      setIsReducingText(false);
    }
  };

  // Fast path: the step order depends on the arm, the photo-analysis confidence and free-cover reuse.
  const [appearanceConfident, setAppearanceConfident] = useState(false);
  const [fromCoverReuse, setFromCoverReuse] = useState(false);
  // The photo-consent tick (R3, unticked by default). Sent with the book request; none for the free-cover photo (R5).
  const [photoConsent, setPhotoConsent] = useState<string | null>(null);
  const drawerOpenedTracked = useRef(false);
  // Fed from a form subscription set up once `form` exists (below); `form` is declared after this point.
  const [traitsComplete, setTraitsComplete] = useState(false);
  const stepSequence = useMemo(
    () => buildMultiStepSequence({ fastPath: variant === 'fast', appearanceConfident, fromCoverReuse, traitsComplete }),
    [variant, appearanceConfident, fromCoverReuse, traitsComplete],
  );

  // Initialize wizard state (no tracking for performance)
  const wizard = useWizardState({
    initialStep: 'story-type',
    enableLogging: false, // Disable wizard logging for performance
    steps: stepSequence,
  });
  
  // MODIFICATION: Auto-set story type to multi and skip the selection step
  useEffect(() => {
    if (!wizard.storyType) {
      wizard.setStoryType('multi');
      // Skip the story-type step immediately
      if (wizard.currentStep === 'story-type') {
        wizard.goToNextStep();
      }
    }
  }, []);
  
  // Funnel: one wizard_step_viewed per visible step, tagged with the arm. Waits for the arm so the
  // first step is never recorded under the wrong one.
  const lastTrackedStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!variantReady) return;
    if (wizard.currentStep === 'story-type' || lastTrackedStepRef.current === wizard.currentStep) return;
    lastTrackedStepRef.current = wizard.currentStep;
    track('wizard_step_viewed', { step: wizard.currentStep, variant });
    if (wizard.currentStep === 'story-creation') track('story_step_viewed', { variant });
  }, [wizard.currentStep, variantReady, variant]);

  // CRITICAL FIX #6: Prevent rapid remounts during step transitions (no tracking)
  // Use a ref to track if we're transitioning to prevent render loops
  const isTransitioningRef = useRef(false);
  const lastStepRef = useRef(wizard.currentStep);
  
  // Detect step changes and set transition flag (minimal logging)
  useEffect(() => {
    if (wizard.currentStep !== lastStepRef.current) {
      // Track step transition globally
      if (typeof window !== 'undefined' && window.__debugState) {
        window.__debugState.transitionHistory.push({
          from: lastStepRef.current,
          to: wizard.currentStep,
          timestamp: Date.now()
        });
      }
      
      isTransitioningRef.current = true;
      lastStepRef.current = wizard.currentStep;
      
      // Reset transition flag after a delay
      const timer = setTimeout(() => {
        isTransitioningRef.current = false;
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [wizard.currentStep]);
  // Form state (no tracking for performance)
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      storyType: undefined, // Let user choose story type
      readingLevel: 'picture',
      language: initialLanguageFromParam(searchParams?.get('language')) ?? getDefaultLanguage().code,
      childFirstName: '',
      childAge: '',
      childGender: undefined,
      hairColor: 'brown', // Set default
      skinColor: 'medium', // Set default
      eyeColor: 'brown', // Set default
      hairTexture: 'straight', // Set default
      // Multi-character defaults
      characterCount: 0,
      mainCharacterIndex: undefined,
      relationshipType: 'siblings',
      characters: [],
      detectedFaces: [],
      // Common fields
      bookTitle: '',
      additionalComments: '',
      dedication: '',
      styleId: 'realistic', // Default to Realistic
      pageCount: 16, // Default to 16 pages (recommended option)
      generateFullBook: false,
      storyContext: {
        culturalSetting: '',
        culturalDetails: ''
      },
      characterPhoto: undefined,
    },
    mode: 'onBlur', // CRITICAL FIX: Changed from 'onChange' to prevent render loops
  });

  // DEBUG: Log form initialization
  useEffect(() => {
    const currentPageCount = form.getValues('pageCount');
    console.log('[WIZARD-FORM] Form initialized with pageCount:', currentPageCount);
  }, []);

  // Free-cover funnel: seed state - set once the ?fromCover=<slug> seed photo has
  // been fetched, so it can be echoed back to create-multi-character-async. The
  // server trusts it only with owner proof: the httpOnly owner cookie (set by the
  // email code check or /api/free-cover/continue), or a legacy owner key in t.
  const [fromCoverSlug, setFromCoverSlug] = useState<string | null>(null);
  const [coverSeed, setCoverSeed] = useState<{ title: string | null; themeId: string | null; coverReady: boolean; faceCount: number | null; photoConsented: boolean } | null>(null);
  const [coverNotReusedReason, setCoverNotReusedReason] = useState<string | null>(null);
  const coverSeedFetchedAt = useRef<number | null>(null);
  const coverSeededIdea = useRef<string | null>(null);
  const coverAppliedSlug = useRef<string | null>(null);
  // The seed photo URL we last wrote; a refresh only touches characterPhoto while it still holds this
  // (a photo the visitor uploaded themselves is never overwritten or cleared).
  const seedPhotoUrlRef = useRef<string | null>(null);
  // Photo consent for this render: the tick, or the free cover's stored consent for its own photo (R5)
  const photoConsentState = wizardPhotoConsent({
    fromCoverSlug,
    photoUrl: form.watch('characterPhoto') as string | undefined,
    seedPhotoUrl: seedPhotoUrlRef.current,
    coverPhotoConsented: coverSeed?.photoConsented === true,
    photoConsent,
  });

  // GET the seed. The first call also records the cover details; a refresh only renews the photo URL
  // (the continuation is applied once, so visitor edits are never overwritten).
  const fetchCoverSeed = async (slug: string): Promise<SeedOutcome> => {
    let outcome: SeedOutcome = 'failed';
    try {
      // Same-origin fetch sends the httpOnly owner cookie.
      const res = await fetch(`/api/free-cover/seed?slug=${encodeURIComponent(slug)}`);
      const body = await res.json().catch(() => null);
      outcome = classifySeedResponse(res.status, body);
      if (outcome === 'ok') {
        const currentPhoto = form.getValues('characterPhoto') as unknown;
        if (seedPhotoUrlRef.current === null || currentPhoto === seedPhotoUrlRef.current || !currentPhoto) {
          form.setValue('characterPhoto', body.photoUrl);
          seedPhotoUrlRef.current = body.photoUrl;
        }
        coverSeedFetchedAt.current = Date.now();
        if (shouldApplyContinuation(coverAppliedSlug.current, slug)) {
          coverAppliedSlug.current = slug;
          setFromCoverSlug(slug);
          setCoverSeed({
            title: typeof body.title === 'string' ? body.title : null,
            themeId: typeof body.themeId === 'string' ? body.themeId : null,
            coverReady: body.coverReady === true,
            faceCount: typeof body.faceCount === 'number' ? body.faceCount : null,
            // R5: the cover holds a stored photo consent (no new tick); false shows the box
            photoConsented: body.photoConsented === true,
          });
        }
      }
    } catch (e) {
      console.warn('[WIZARD] free-cover seed failed (non-fatal)', e);
    }
    return outcome;
  };

  // One place for every consumer of the seed photo URL (face detection, analysis, story, feedback, preview).
  // The signed URL lasts 15 minutes; renew it if it may have lapsed, and fall back to a normal flow if the
  // photo is gone or no longer ours.
  const getFreshCoverPhotoUrl = async (): Promise<string | undefined> => {
    if (fromCoverSlug && seedPhotoIsStale(coverSeedFetchedAt.current)) {
      const effect = afterSeedRefresh(await fetchCoverSeed(fromCoverSlug));
      if (effect.dropReuse) {
        setFromCoverReuse(false);
        setFromCoverSlug(null);
        setCoverSeed(null);
      }
      if (effect.clearPhoto && form.getValues('characterPhoto') === seedPhotoUrlRef.current) form.setValue('characterPhoto', undefined);
      if (effect.toastExpired) {
        toast({ title: 'Your photo has expired', description: 'Your photo has expired - please upload it again to continue.' });
      }
    }
    return form.getValues('characterPhoto');
  };

  // Free-cover funnel: on mount, if arriving via a "Turn this into a full
  // book" link (?fromCover=<slug>), fetch the short-lived signed
  // photo URL and pre-attach it, following the existing
  // form.setValue('characterPhoto', ...) pattern used elsewhere in this file.
  // Failures are silently swallowed (the wizard just starts normally), except an expired photo (410),
  // which tells the visitor to upload it again.
  useEffect(() => {
    const slug = searchParams?.get('fromCover');
    const t = searchParams?.get('t');
    if (!slug) return;
    // Never leave a key in the address bar, history or page analytics (Umami records the query string).
    if (t && typeof window !== 'undefined') {
      // A legacy owner key: exchange it once for the owner cookie (the continue route redirects back here
      // without the key), instead of ever sending it to the seed route.
      window.location.replace(`/api/free-cover/continue?slug=${encodeURIComponent(slug)}&t=${encodeURIComponent(t)}`);
      return;
    }

    (async () => {
      const result = await fetchCoverSeed(slug);
      if (afterSeedRefresh(result).toastExpired) {
        toast({
          title: 'Your photo has expired',
          description: 'Your photo has expired - please upload it again to continue.',
        });
      }
    })();
  }, []); // Run once on mount

  // AI analysis state for single character (no tracking)
  const [aiAnalysis, setAiAnalysis] = useState<any>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [coverStoryTemplateId, setCoverStoryTemplateId] = useState<string | null>(null);

  // Invalidate the cached story when the selected template/story option changes.
  // Without this, going back and choosing a different story (e.g. Ocean Friends
  // instead of Swimming Adventure) and clicking "Create Story" would skip
  // regeneration and show the previously generated story (stale-cache bug):
  // the story-creation guard only checks whether a storyPreview exists.
  // Generalised: the preview is dropped whenever any input that feeds generation changes
  // (concept text, template, language, characters), so a rewritten concept never reuses an old story.
  useEffect(() => {
    const sync = () => setTraitsComplete(hasAllRequiredTraits(form.getValues('characters') as any));
    sync();
    const sub = form.watch((_v, info) => { if (!info.name || info.name.startsWith('characters')) sync(); });
    return () => sub.unsubscribe();
  }, [form]);
  const [watchedComments, watchedLanguage, watchedCharacters, watchedReadingLevel] = form.watch(['additionalComments', 'language', 'characters', 'readingLevel']);
  const storyKey = storyInputsKey({
    additionalComments: watchedComments,
    templateId: selectedTemplate?.id,
    language: watchedLanguage,
    readingLevel: watchedReadingLevel,
    characterNames: (watchedCharacters as any[] | undefined)?.map((c) => c?.name),
    characterAges: (watchedCharacters as any[] | undefined)?.map((c) => c?.age),
    characterGenders: (watchedCharacters as any[] | undefined)?.map((c) => c?.gender),
  });
  const previewKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!storyPreview) { previewKeyRef.current = null; return; }
    if (previewKeyRef.current === null) { previewKeyRef.current = storyKey; return; }
    if (previewKeyRef.current !== storyKey) { previewKeyRef.current = null; setStoryPreview(null); }
  }, [storyPreview, storyKey]);

  // Free-cover continuation (fast arm only): the cover becomes the book cover, so the style is fixed to
  // the cover's (Realistic) and the story starts from the cover's title and theme. Reuse is confirmed
  // again once the faces are selected (canReuseCover in handleNext).
  useEffect(() => {
    if (!variantReady || variant !== 'fast' || !coverSeed?.coverReady) return;
    setFromCoverReuse(true);
    form.setValue('styleId', 'realistic');
    if (coverSeed.title) form.setValue('bookTitle', coverSeed.title);
    const seed = coverStorySeed(coverSeed.themeId);
    if (seed) {
      form.setValue('additionalComments', seed.storyIdea);
      coverSeededIdea.current = seed.storyIdea;
      setSelectedTemplate(seed.template);
      setCoverStoryTemplateId(seed.template?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantReady, variant, coverSeed]);

  // AI analysis state for multi-character (no tracking)
  const [isAnalyzingMultiChar, setIsAnalyzingMultiChar] = useState(false);
  const [multiCharAnalysisComplete, setMultiCharAnalysisComplete] = useState(false);
  
  // Navigation state (no tracking)
  const [isNavigating, setIsNavigating] = useState(false);
  
  // Real-time validation state (no tracking)
  const isStepValid = useStepValidation(wizard.currentStep, form, wizard.storyType, selectedTemplate);
  
  // Remove problematic form watcher - SampleShowcaseBanner will handle its own style watching
  
  // Memoize AI analysis prop to prevent unnecessary re-renders
  // Use JSON.stringify to create a stable dependency
  const aiAnalysisKey = JSON.stringify({
    gender: aiAnalysis?.gender,
    age: aiAnalysis?.estimatedAge || aiAnalysis?.age,
    hairColor: aiAnalysis?.hairColor,
    skinColor: aiAnalysis?.skinColor,
    eyeColor: aiAnalysis?.eyeColor,
    hairTexture: aiAnalysis?.hairTexture,
    confidence: aiAnalysis?.confidence
  });
  
  const stableAiAnalysisProp = useMemo(() => ({
    gender: aiAnalysis?.gender,
    age: aiAnalysis?.estimatedAge || aiAnalysis?.age,
    estimatedAge: aiAnalysis?.estimatedAge || aiAnalysis?.age,
    hairColor: aiAnalysis?.hairColor,
    hairStyle: aiAnalysis?.hairStyle || 'short',
    skinColor: aiAnalysis?.skinColor,
    eyeColor: aiAnalysis?.eyeColor,
    hairTexture: aiAnalysis?.hairTexture,
    confidence: aiAnalysis?.confidence || 0.8
  }), [aiAnalysisKey]);
  
  // Simplified loading overlay props - no useMemo to prevent subscription loops
  const loadingOverlayProps = {
    childName: form.getValues().childFirstName,
    characterCount: form.getValues().characters?.length || 0,
    characterNames: form.getValues().characters?.map(c => c.name) || [],
    mainCharacterName: form.getValues().characters?.find((_, i) => i === form.getValues().mainCharacterIndex)?.name
  };

  // Simplified navigation label - no useMemo to prevent subscription loops
  const nextLabel = wizard.currentStep === 'story-type' 
    ? (!form.getValues().storyType ? 'Select' : 'Next')
    : wizard.currentStep === 'story-creation'
    ? 'Generate Story'  // Always show "Generate Story" since we auto-advance
    : 'Next';

  // Initialize session ID (no tracking)
  useEffect(() => {
    const id = getOrCreateSessionId();
    setSessionId(id);
    console.log('[WIZARD] Session ID initialized:', id);
  }, []);

  // Log that wizard UI is being used (no tracking)
  useEffect(() => {
    wizardLogger.featureFlag(true);
  }, []);

  // Cleanup polling on unmount (no tracking)
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        console.log('[WIZARD] Cleaning up polling interval on unmount');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      isPollingRef.current = false; // Clear polling flag
    };
  }, []);
  
  // Cleanup wake lock on unmount (no tracking)
  useEffect(() => {
    return () => {
      // Release wake lock if component unmounts while processing
      releaseWakeLock();
    };
  }, [releaseWakeLock]);

  // Cover-notify escape hatch: show after COVER_NOTIFY_DELAY_MS during preview/cover generation
  useEffect(() => {
    if (isLoading && (currentOperation === 'preview' || currentOperation === 'cover') && previewRequestId && sessionId) {
      if (coverNotifyTimerRef.current) {
        clearTimeout(coverNotifyTimerRef.current);
      }
      coverNotifyTimerRef.current = setTimeout(() => {
        setShowCoverNotify(true);
      }, COVER_NOTIFY_DELAY_MS);
    } else {
      if (coverNotifyTimerRef.current) {
        clearTimeout(coverNotifyTimerRef.current);
        coverNotifyTimerRef.current = null;
      }
      setShowCoverNotify(false);
      setCoverNotifyDismissed(false);
    }
    return () => {
      if (coverNotifyTimerRef.current) {
        clearTimeout(coverNotifyTimerRef.current);
      }
    };
  }, [isLoading, currentOperation, previewRequestId, sessionId]);

  // Handle story feedback submission
  const handleSubmitFeedback = async () => {
    if (!feedback.trim() || !storyPreview) return;
    
    setIsSubmittingFeedback(true);
    
    try {
      await getFreshCoverPhotoUrl();
      const data = form.getValues();
      const response = await fetchWithToast('/api/books/update-story', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId && { 'x-session-id': sessionId })
        },
        body: JSON.stringify({
          childDetails: {
            firstName: data.childFirstName || '',
            gender: data.childGender || Gender.OTHER,
            age: parseInt(data.childAge || '5'),
            hairColor: data.hairColor || 'brown',
            skinColor: data.skinColor || 'medium',
            language: data.language,
            photoUrl: data.characterPhoto
          },
          style: styles.find(s => s.id === data.styleId),
          currentStory: storyPreview,
          bookTitle: data.bookTitle,
          additionalComments: data.additionalComments,
          feedback: feedback,
          photoUrls: data.characterPhoto ? [data.characterPhoto] : [],
          storyContext: data.storyContext
        }),
      }, toast);
      
      if (response?.storyPages) {
        // Validate and reduce page text if needed before setting preview
        const optimizedPages = await validateAndReducePageText(response.storyPages, form.getValues('readingLevel'));
        setStoryPreview(optimizedPages);
        toast({
          title: "Story Updated!",
          description: "We've updated the story based on your feedback."
        });
        setFeedback("");
      }
    } catch (error) {
      console.error('[WIZARD] Error submitting feedback:', error);
      toast({ 
        title: "Error", 
        description: "Failed to update story. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Handle final book creation (preview)
  const handleCreateBookPreview = async () => {
    if (!storyPreview || !form.getValues('styleId')) {
      toast({ 
        title: "Error", 
        description: "Story preview or style is missing.", 
        variant: "destructive" 
      });
      return;
    }

    // A dedication edited on the review step bypasses the story-creation check; block it here too.
    const dedicationBlocking = getDedicationIssue(form.getValues());
    if (dedicationBlocking?.blocking) {
      toast({ title: 'Dedication', description: dedicationBlocking.message, variant: 'destructive' });
      return;
    }

    // CRITICAL: Stop any existing story polling before starting preview generation
    if (pollingIntervalRef.current) {
      console.log('[WIZARD] Stopping story polling before preview generation');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      isPollingRef.current = false;
    }

    // For multi-character stories, we pass the storyBookId to the backend
    // The backend will handle waiting for visual registry if needed

    setIsLoading(true);
    setCurrentOperation('preview');
    track('cover_preview_requested', { variant, style: form.getValues('styleId') });
    if (fromCoverReuse) track('free_cover_reused', { variant });
    await getFreshCoverPhotoUrl();

    // Photo consent (R3): never send the book request unticked (the server would refuse it). Inherited for the
    // free cover's own photo when the cover holds a stored consent (R5).
    if (!photoConsentState.canCreate) {
      toast({ title: 'Photo permission needed', description: copy('C-04'), variant: 'destructive' });
      // Back to the photo step, where the box is
      wizard.goToStep('photo-upload');
      setIsLoading(false);
      setCurrentOperation(null);
      return;
    }

    // Request wake lock to prevent screen from sleeping during book generation
    await requestWakeLock('Book preview generation');
    
    try {
      const data = form.getValues();
      const style = styles.find(s => s.id === data.styleId);
      
      // Validate style exists
      if (!style) {
        toast({ 
          title: "Error", 
          description: "Selected style not found.", 
          variant: "destructive" 
        });
        setIsLoading(false);
        setCurrentOperation(null);
        return;
      }
      
      // CRITICAL FIX: ALWAYS use multi-character flow, even for single person photos
      // This ensures supporting elements (robots, dragons, etc.) are properly included
      const effectiveStoryType = 'multi'; // Force multi-character path
      
      // Save character count info to localStorage
      if (wizard.storyType === 'single') {
        // Even though user selected single, we process as multi with 1 character
        localStorage.setItem('isMultiCharacterBook', 'true'); // Changed to true
        localStorage.setItem('characterCount', '1');
        console.log('[WIZARD] Single selection but using multi-character flow');
      } else if (wizard.storyType === 'multi') {
        const charCount = data.characters?.length || data.characterCount || 2;
        localStorage.setItem('isMultiCharacterBook', 'true');
        localStorage.setItem('characterCount', String(charCount));
        console.log('[WIZARD] Multi-character info saved:', { characterCount: charCount });
      }
      
      // ALWAYS use multi-character request body structure
      const requestBody = {
        // Multi-character request body - NO bookId
        bookTitle: data.bookTitle || data.title, // Handle both field names
        style: style,
        storyPages: storyPreview,
        photoUrl: data.characterPhoto,
        language: data.language,
        additionalComments: data.additionalComments,
        dedication: data.dedication,
        pageCount: data.pageCount,
        readingLevel: data.readingLevel, // saved on the paid book (additional_comments)
        storyContext: data.storyContext,
        generateFullBook: data.generateFullBook,
        storyBookId: currentBookId, // CRITICAL: Pass the story book ID for visual registry lookup
        ...(fromCoverSlug && { fromCoverSlug, reuseFreeCover: fromCoverReuse }), // Free-cover funnel (owner cookie checked server-side)
        ...photoConsentJson(photoConsent), // R3; without it a fromCoverSlug book inherits the cover's consent (R5)
        characters: wizard.storyType === 'single'
          ? [{
              // Convert single character data to multi-character format
              name: data.childFirstName || 'Child',
              age: parseInt(data.childAge || '5'),
              gender: data.childGender || Gender.OTHER,
              hairColor: data.hairColor || 'brown',
              skinColor: data.skinColor || 'medium',
              relationship: 'self',
              isMainCharacter: true,
              bbox: null // Will be detected from photo
            }]
          : data.characters?.map((char, index) => ({
              ...char,
              age: parseInt(char.age),
              heightInches: char.heightInches ? parseInt(String(char.heightInches), 10) : undefined,
              isMainCharacter: index === data.mainCharacterIndex
            })),
        characterOutfits: characterOutfits,
        photoFeatures: photoFeatures,
        faceDetectionResults: wizard.storyType === 'multi' 
          ? data.characters?.reduce((acc, char) => {
              if (char.bbox) {
                acc[char.name] = char.bbox;
              }
              return acc;
            }, {} as Record<string, any>)
          : {} // For single, face detection will happen server-side
      };
      
      const response = await fetchWithToast(
        '/api/books/create-multi-character-async', // ALWAYS use multi-character endpoint
        {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionId && { 'x-session-id': sessionId })
        },
        body: JSON.stringify(requestBody),
      }, toast);
      
      if (response?.requestId) {
        setPreviewRequestId(response.requestId);
        startPollingForPreview(response.requestId);
        
        toast({ 
          title: "Preview Generation Started", 
          description: `Creating your book preview. This usually takes ${WIZARD_COVER_PREVIEW_TIME}.` 
        });
      } else {
        // fetchWithToast already toasted the server's message (e.g. a 400 dedication error).
        const err: any = new Error(response?.error || "Could not start preview creation.");
        err.alreadyNotified = !!response?.error; // fetchWithToast toasts every { error } response
        throw err;
      }
    } catch (error: any) {
      console.error('[WIZARD] Error creating preview:', error);
      setIsLoading(false);
      setCurrentOperation(null);
      
      // Release wake lock on error
      await releaseWakeLock();
      
      // fetchWithToast already showed the server's message; only toast failures nobody has reported.
      if (!error?.alreadyNotified) {
        toast({ 
          title: "Error", 
          description: "Failed to create preview. Please try again.", 
          variant: "destructive" 
        });
      }
    }
  };
  
  // Polling function for story generation
  const startPollingForStory = (requestId: string) => {
    // Generate unique ID for this polling session
    const pollSessionId = `poll_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`[WIZARD] Starting new polling session: ${pollSessionId} for request: ${requestId}`);
    
    // Prevent multiple polling loops
    if (isPollingRef.current) {
      console.log('[WIZARD] WARNING: Already polling, skipping new polling request');
      return;
    }
    isPollingRef.current = true;
    
    // Clear any existing polling interval first
    if (pollingIntervalRef.current) {
      console.log('[WIZARD] WARNING: Previous polling interval exists, clearing it');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    let pollCount = 0;
    const maxPolls = wizard.storyType === 'multi' ? 80 : 60; // More time for multi-character
    let coverShown = false; // Track if we've shown the cover
    let stopPolling = false; // Flag to stop polling
    
    const interval = setInterval(async () => {
      // Check if we should stop polling
      if (stopPolling) {
        console.log(`[WIZARD][${pollSessionId}] Stop polling flag set, clearing interval`);
        clearInterval(interval);
        if (pollingIntervalRef.current === interval) {
          pollingIntervalRef.current = null;
        }
        isPollingRef.current = false; // Clear polling flag
        return;
      }
      
      pollCount++;
      console.log(`[WIZARD][${pollSessionId}] Poll #${pollCount} starting`);
      
      try {
        const response = await fetch(`/api/books/story-status?requestId=${requestId}`);
        const data = await response.json();
        
        console.log(`[WIZARD][${pollSessionId}] Poll #${pollCount}: Status = ${data.status}`);
        
        // Handle cover-ready state (NEW)
        if (data.status === 'cover_ready' && !coverShown) {
          console.log('[COVER-FIRST] Cover is ready from polling!');
          if (data.coverData) {
            setCoverData(data.coverData);
            setCurrentOperation('cover');
            coverShown = true;
            
            // Update UI to show cover is ready
            toast({ 
              title: "✨ Cover Ready!", 
              description: `Your story is still being written (usually ${STORY_TEXT_TIME}).`,
              duration: 3000
            });
          }
          // Continue polling for full story
        } else if (data.status === 'story_text_ready') {
          // Story text is ready - that's all we need for the preview!
          console.log(`[WIZARD][${pollSessionId}] Story text ready - STOPPING POLLING`);
          
          // STOP POLLING - we have everything we need for story preview
          stopPolling = true;
          isPollingRef.current = false;
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          
          if (data.storyPages && data.storyPages.length > 0) {
            setIsLoading(false);
            setCurrentOperation(null);
            
            track('story_generated', { language: form.getValues('language') || undefined, page_count: data.storyPages.length, variant, reading_level: form.getValues('readingLevel') || 'picture' });
            console.log(`[WIZARD][${pollSessionId}] Story pages received:`, {
              totalPages: data.storyPages?.length,
              pageNumbers: data.storyPages?.map(p => p.pageNumber || p.page),
              hasPage0: data.storyPages?.some(p => (p.pageNumber === 0 || p.page === 0))
            });

            // Validate and reduce page text if needed before setting preview
            const optimizedPages = await validateAndReducePageText(data.storyPages, form.getValues('readingLevel'));

            // Only update story preview if it's different (to prevent unnecessary re-renders)
            if (!storyPreview || JSON.stringify(storyPreview) !== JSON.stringify(optimizedPages)) {
              setStoryPreview(optimizedPages);
            }

            if (data.bookId) {
              setCurrentBookId(data.bookId);
            }

            // Auto-advance to story preview when ready
            // This provides better UX than requiring another click
            if (wizard.currentStep !== 'story-preview') {
              console.log(`[WIZARD][${pollSessionId}] Auto-advancing to story-preview`);
              wizard.goToNextStep();
            }

            toast({
              title: "✨ Story Ready!",
              description: "Your personalized story has been generated!"
            });
            
            // Visual registry will complete in background - we don't care about it here
            console.log(`[WIZARD][${pollSessionId}] Visual registry will complete in background`);
          } else {
            console.error(`[WIZARD][${pollSessionId}] No story pages in story_text_ready response`);
            toast({ 
              title: "Error", 
              description: "Story generation failed. Please try again.", 
              variant: "destructive" 
            });
          }
        } else if (data.status === 'completed' && data.storyPages) {
          track('story_generated', { language: form.getValues('language') || undefined, page_count: Array.isArray(data.storyPages) ? data.storyPages.length : undefined, reading_level: form.getValues('readingLevel') || 'picture' });
          // For non-split generation (single character or full multi-character generation)
          stopPolling = true;
          isPollingRef.current = false; // Clear polling flag
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          setIsLoading(false);
          setCurrentOperation(null);
          setCoverData(null); // Clear cover data since we have full story
          
          // Release wake lock when story generation completes
          await releaseWakeLock();
          
          // Debug: Log what we received
          console.log('[WIZARD] Story pages received (completed):', {
            totalPages: data.storyPages?.length,
            pageNumbers: data.storyPages?.map(p => p.pageNumber || p.page),
            hasPage0: data.storyPages?.some(p => (p.pageNumber === 0 || p.page === 0))
          });

          // Validate and reduce page text if needed before setting preview
          const optimizedPages = await validateAndReducePageText(data.storyPages, form.getValues('readingLevel'));
          setStoryPreview(optimizedPages);

          if (data.childPhotoUrl) {
            form.setValue('characterPhoto', data.childPhotoUrl);
          }
          if (data.bookId) {
            setCurrentBookId(data.bookId);
          }

          // Auto-advance to story preview when ready
          if (wizard.currentStep !== 'story-preview') {
            console.log('[WIZARD] Auto-advancing to story-preview');
            wizard.goToNextStep();
          }

          toast({
            title: "✨ Story Generated!",
            description: "Your personalized story is ready!"
          });
        } else if (data.status === 'error') {
          stopPolling = true;
          isPollingRef.current = false; // Clear polling flag
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          setIsLoading(false);
          setCurrentOperation(null);
          
          // Release wake lock on error
          await releaseWakeLock();
          
          // Simplify error message for user display
          let storyErrorMessage = data.error || "An error occurred during story generation.";
          if (storyErrorMessage.length > 100 || storyErrorMessage.includes('{')) {
            storyErrorMessage = "Story generation failed. Please try again.";
          }
          toast({
            title: "Story Generation Failed",
            description: storyErrorMessage,
            variant: "destructive",
            duration: 8000
          });
        } else if (pollCount >= maxPolls) {
          stopPolling = true;
          isPollingRef.current = false; // Clear polling flag
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          setIsLoading(false);
          setCurrentOperation(null);
          
          // Release wake lock on timeout
          await releaseWakeLock();
          
          toast({ 
            title: "Story Generation Timeout", 
            description: "Story generation is taking unusually long. Please try again later.", 
            variant: "destructive",
            duration: 10000
          });
        }
      } catch (error) {
        console.error(`[WIZARD][${pollSessionId}] Error polling for story status:`, error);
        if (pollCount >= maxPolls) {
          stopPolling = true;
          isPollingRef.current = false; // Clear polling flag
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          setIsLoading(false);
          setCurrentOperation(null);
          
          toast({ 
            title: "Connection Error", 
            description: "Lost connection while generating your story.", 
            variant: "destructive"
          });
        }
      }
    }, 3000);
    
    pollingIntervalRef.current = interval; // Store the interval in the ref
  };
  
  // Polling function for preview generation
  const startPollingForPreview = (requestId: string) => {
    // Generate unique ID for this polling session
    const pollSessionId = `preview_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`[WIZARD] Starting preview polling session: ${pollSessionId} for request: ${requestId}`);
    
    // Prevent multiple polling loops
    if (isPollingRef.current) {
      console.log('[WIZARD] WARNING: Already polling, stopping previous poll first');
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    isPollingRef.current = true;
    
    // Clear any existing polling interval first
    if (pollingIntervalRef.current) {
      console.log('[WIZARD] Clearing existing polling interval before starting preview polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    let pollCount = 0;
    const maxPolls = wizard.storyType === 'multi' ? 100 : 60;
    
    const interval = setInterval(async () => {
      pollCount++;
      
      try {
        const response = await fetch(`/api/books/story-status?requestId=${requestId}`);
        const data = await response.json();
        
        console.log(`[WIZARD][${pollSessionId}] Preview poll #${pollCount}: Status = ${data.status}`);
        
        if (data.status === 'completed' && data.bookId) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          isPollingRef.current = false; // Clear polling flag
          setIsLoading(false);
          setCurrentOperation(null);
          
          // Release wake lock when generation completes
          await releaseWakeLock();
          
          toast({ 
            title: "Preview Ready!", 
            description: "Redirecting to book preview...", 
            variant: "success" 
          });
          
          router.push(`/book/${data.bookId}/preview`);
        } else if (data.status === 'error') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          isPollingRef.current = false; // Clear polling flag
          setIsLoading(false);
          setCurrentOperation(null);
          
          // Release wake lock on error
          await releaseWakeLock();
          
          // Simplify error message for user display
          let errorMessage = data.error || "An error occurred during preview generation.";
          if (errorMessage.length > 100 || errorMessage.includes('{')) {
            errorMessage = "Image generation failed. Please try again.";
          }
          toast({
            title: "Preview Generation Failed",
            description: errorMessage,
            variant: "destructive",
            duration: 8000
          });
        } else if (pollCount >= maxPolls) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          isPollingRef.current = false; // Clear polling flag
          setIsLoading(false);
          setCurrentOperation(null);
          
          // Release wake lock on timeout
          await releaseWakeLock();
          
          toast({ 
            title: "Preview Generation Timeout", 
            description: "Preview generation is taking unusually long. Please try again later.", 
            variant: "destructive",
            duration: 10000
          });
        }
      } catch (error) {
        console.error('[WIZARD] Error polling for preview status:', error);
        if (pollCount >= maxPolls) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          clearInterval(interval);
          isPollingRef.current = false; // Clear polling flag
          setIsLoading(false);
          setCurrentOperation(null);
          
          // Release wake lock on error
          await releaseWakeLock();
          
          toast({ 
            title: "Connection Error", 
            description: "Lost connection while generating your preview.", 
            variant: "destructive"
          });
        }
      }
    }, 3000);
    
    pollingIntervalRef.current = interval; // Store the interval in the ref
  };
  
  // Validate current step
  const validateCurrentStep = async (): Promise<boolean> => {
    const currentStep = wizard.currentStep;
    let fieldsToValidate: (keyof FormData)[] = [];
    
    console.log('[WIZARD] validateCurrentStep for:', currentStep);
    
    // Helper function for handling face confirmation in multi-character flow
    // NOTE: AI analysis is now triggered AFTER step transition in handleNext
    // to prevent AbortErrors from navigation-induced re-renders
    const handleFacesConfirmed = async (faces: any[], mainCharacterIndex: number) => {
      console.log('[WIZARD] Faces confirmed:', faces.length, 'main character index:', mainCharacterIndex);
      track('faces_confirmed', { face_count: faces.length });

      // Store face data in form
      form.setValue('detectedFaces', faces);
      form.setValue('characterCount', faces.length);
      form.setValue('mainCharacterIndex', mainCharacterIndex);

      // Same faces as before (Back to the photo step, then Next): keep the analysed/entered traits.
      // Different faces: rebuild blank and make the analysis run again.
      const existingChars = form.getValues('characters') as any[];
      if (!shouldResetCharacters(existingChars, faces)) {
        // Same people: keep traits, but carry over a rename or new thumbnail from the faces step.
        form.setValue('characters', mergeFaceIdentity(existingChars, faces) as any);
        setCharacterCount(faces.length);
        return;
      }
      setMultiCharAnalysisComplete(false);
      setAppearanceConfident(false);

      // Initialize characters array with detected faces
      // CRITICAL FIX: Use tight bbox for better facial likeness, not expanded_bbox
      const newCharacters = faces.map((face, index) => ({
        name: face.name || '',
        age: '',
        gender: '',
        skinTone: '',
        hairColor: '',
        eyeColor: '',
        hairTexture: '',
        bbox: face.bbox,  // Use tight face detection coordinates
        thumbnail: face.thumbnail || ''
      }));
      form.setValue('characters', newCharacters);

      // AI analysis is now handled separately after step transition
      // This prevents AbortErrors that occurred when the fetch was
      // running during React's re-render/unmount cycle

      setCharacterCount(faces.length);
    };
    
    switch (currentStep) {
      case 'story-type':
        // Check if story type is selected
        const storyType = form.getValues('storyType');
        if (!storyType) {
          // Trigger validation to show error
          await form.trigger(['storyType']);
          return false;
        }
        return true;
        
      case 'photo-upload':
        // Check if photo is uploaded
        const photoUrl = form.getValues('characterPhoto');
        console.log('[WIZARD] Photo URL:', photoUrl);
        
        if (!photoUrl) {
          console.log('[WIZARD] No photo URL found');
          return false;
        }
        
        // For multi-character stories, also check if faces have been detected
        if (wizard.storyType === 'multi') {
          const detectedFaces = form.getValues('detectedFaces') || [];
          const charactersArray = form.getValues('characters') || [];
          const charCount = form.getValues('characterCount') || 0;
          
          console.log('[WIZARD] Multi-character validation START:', {
            detectedFacesLength: detectedFaces.length,
            charactersLength: charactersArray.length,
            characterCount: charCount,
            hasRef: !!(window as any).__faceDetectionRef
          });
          
          // Check if face data is available from the wizard component
          const faceDetectionRef = (window as any).__faceDetectionRef;
          if (faceDetectionRef && faceDetectionRef.current) {
            console.log('[WIZARD] Found face detection ref, validating...');
            const faceData = faceDetectionRef.current.validateAndGetData();
            console.log('[WIZARD] Face data validation result:', faceData);
            if (faceData) {
              // Call handleFacesConfirmed with the data
              await handleFacesConfirmed(faceData.faces, faceData.mainIndex);
              // Mark as valid and complete after face confirmation
              wizard.markStepCompleted();
              console.log('[WIZARD] Face confirmation complete, returning true');
              return true;
            } else {
              // Validation failed (toast already shown by component)
              console.log('[WIZARD] Face validation failed');
              return false;
            }
          }
          
          // If we already have face data, it's valid
          if (detectedFaces.length > 0 && charactersArray.length > 0 && charCount > 0) {
            wizard.markStepCompleted();
            console.log('[WIZARD] Already have face data, returning true');
            return true;
          }
          
          console.log('[WIZARD] Face detection not complete for multi-character story');
          return false;
        }
        
        // Mark as valid and complete
        wizard.markStepCompleted();
        return true;
        
      case 'character-details-review': {
        // Check if name and age are filled
        const name = form.getValues('childFirstName');
        const age = form.getValues('childAge');
        const gender = form.getValues('childGender');
        if (!name || !age || !gender) {
          console.log('[WIZARD] Character details missing:', { name: !!name, age: !!age, gender: !!gender });
          return false;
        }
        wizard.markStepCompleted();
        return true;
      }
        
      case 'appearance-review':
        // For appearance review, we just need to ensure AI analysis is complete
        wizard.markStepCompleted();
        return true;
        
      case 'story-selection':
        // Check if a template and page count are selected
        if (!selectedTemplate) {
          console.log('[WIZARD] No template selected');
          return false;
        }
        wizard.markStepCompleted();
        return true;
        
      case 'multi-story-selection':
        // Check if a template and page count are selected
        if (!selectedTemplate) {
          console.log('[WIZARD] No template selected');
          return false;
        }
        wizard.markStepCompleted();
        return true;
        
      case 'character-details': {
        fieldsToValidate = ['childFirstName', 'childAge', 'childGender'];
        break;
      }
        
      case 'multi-character-details': {
        // Validate characters array
        const characters = form.getValues('characters') || [];
        const isValid = characters.length > 0 && 
          characters.every(char => char.name && char.age && char.gender);
        if (isValid) {
          wizard.markStepCompleted();
        }
        return isValid;
      }
        
      case 'appearance':
        fieldsToValidate = ['hairColor', 'skinColor'];
        break;
        
      case 'multi-character-appearance':
        // Validate characters appearance
        const chars = form.getValues('characters') || [];
        const appearanceValid = chars.length > 0 && 
          chars.every(char => char.skinTone && char.hairColor && char.eyeColor && char.hairTexture);
        if (appearanceValid) {
          wizard.markStepCompleted();
        }
        return appearanceValid;
        
      case 'style':
        fieldsToValidate = ['styleId'];
        break;
        
      case 'story-creation':
        fieldsToValidate = ['language', 'bookTitle', 'additionalComments'];
        break;
        
      case 'story-preview':
        // All validation should be complete by this point
        return true;
    }
    
    // Trigger validation for specific fields
    const result = await form.trigger(fieldsToValidate as any);
    
    if (result) {
      wizard.markStepCompleted();
    }
    
    wizardLogger.validation(currentStep, result);
    return result;
  };

  // Dedication problems, from the same rules the server applies. The 'Emoji...' message is a
  // non-blocking notice (the printed text simply omits them).
  const getDedicationIssue = (values: { dedication?: string; language?: string }): { message: string; blocking: boolean } | null => {
    // Normalise the raw text the way the sanitiser does (NFC, CRLF->LF, spaces/tabs) so pure
    // whitespace differences don't look like removed emoji.
    const raw = (values.dedication ?? '')
      .normalize('NFC')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line) => line.replace(/[ \t\u00A0]+/g, ' ').trim())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    const r = sanitizeDedication(values.dedication);
    if (!r.ok) return { message: r.error, blocking: true };
    if (r.value && !dedicationFitsBook(r.value, values.language || 'en'))
      return { message: 'Please write the dedication in the same alphabet as your book, so it prints correctly.', blocking: true };
    if (raw && r.value !== raw)
      return { message: 'Emoji and special symbols will be left out of the printed page.', blocking: false };
    return null;
  };

  // Handle navigation
  const handleNext = async () => {
    console.log('[WIZARD] handleNext called, current step:', wizard.currentStep);
    console.log("[WIZARD] Form values:", form.getValues());
    console.log("[WIZARD] Form errors:", form.formState.errors);
    
    setIsNavigating(true);
    
    try {
      console.log('[WIZARD] Form values:', form.getValues());
      console.log('[WIZARD] Form errors:', form.formState.errors);

      if (wizard.currentStep === 'photo-upload') await getFreshCoverPhotoUrl();
      const isValid = await validateCurrentStep();
      console.log('[WIZARD] Validation result:', isValid);

      if (isValid) {
        // Special handling for multi-character photo upload - AI analysis is MANDATORY
        // We must complete AI analysis before allowing navigation to ensure accurate character traits
        if (wizard.currentStep === "photo-upload" && wizard.storyType === "multi") {
          const detectedFaces = form.getValues("detectedFaces") || [];
          const characters = form.getValues("characters") || [];
          const selectedFaces = detectedFaces.filter((face: any) => face.selected);

          // Now characters.length should be > 0 because handleFacesConfirmed was called in validateCurrentStep
          if (selectedFaces.length > 0 && characters.length > 0 && !multiCharAnalysisComplete) {
            console.log("[WIZARD] Starting MANDATORY AI analysis - blocking navigation until complete");
            const triggerAIAnalysis = (window as any).__triggerAIAnalysis;
            if (triggerAIAnalysis) {
              await getFreshCoverPhotoUrl();
              setIsAnalyzingMultiChar(true);
              try {
                // AWAIT the AI analysis - this is mandatory, not optional
                const result = await triggerAIAnalysis(selectedFaces);
                setIsAnalyzingMultiChar(false);

                if (result) {
                  setMultiCharAnalysisComplete(true);
                  console.log("[WIZARD] AI analysis complete - proceeding with navigation");
                  const confident = isAppearancePrefillConfident(result);
                  setAppearanceConfident(confident);
                  if (variant === 'fast') {
                    // Reuse the free cover only if it shows exactly the people selected.
                    const reuse = fromCoverReuse && canReuseCover(coverSeed, selectedFaces.length);
                    if (reuse !== fromCoverReuse) {
                      setFromCoverReuse(reuse);
                      setCoverNotReusedReason(
                        `Your free cover shows ${coverSeed?.faceCount ?? 'a different number of'} ${coverSeed?.faceCount === 1 ? 'person' : 'people'}, but this book has ${selectedFaces.length}, so we'll make a new cover instead.`,
                      );
                      // The seeded idea says the cover already exists; drop it so the story isn't written around it.
                      if (coverSeededIdea.current && form.getValues('additionalComments') === coverSeededIdea.current) {
                        form.setValue('additionalComments', '');
                        setSelectedTemplate(null);
                        setCoverStoryTemplateId(null);
                      }
                    }
                    // The sequence in this closure was built before the confidence was known, so work out
                    // the real next step and jump to it instead of goToNextStep().
                    const seq = buildMultiStepSequence({ fastPath: true, appearanceConfident: confident, fromCoverReuse: reuse, traitsComplete: hasAllRequiredTraits(result as any) });
                    wizard.markStepCompleted('photo-upload');
                    wizard.goToStep(nextStepAfter(seq, 'photo-upload'));
                    return; // the finally block clears isNavigating
                  }
                } else {
                  // Analysis returned undefined after all retries failed
                  console.error("[WIZARD] AI analysis failed after all retries");
                  toast({
                    title: "Photo Analysis Failed",
                    description: "We couldn't analyze this photo. Please try a different photo or contact support.",
                    variant: "destructive"
                  });
                  setIsNavigating(false);
                  return; // Block navigation - don't proceed without AI analysis
                }
              } catch (err: any) {
                console.error("[WIZARD] AI analysis error:", err);
                setIsAnalyzingMultiChar(false);
                toast({
                  title: "Photo Analysis Failed",
                  description: "We couldn't analyze this photo. Please try a different photo or contact support.",
                  variant: "destructive"
                });
                setIsNavigating(false);
                return; // Block navigation - don't proceed without AI analysis
              }
            }
          }
        }

        if (wizard.currentStep === 'story-type') {
          const storyType = form.getValues('storyType');
          if (storyType) {
            wizard.setStoryType(storyType);
          }
        } else if (wizard.currentStep === 'story-creation') {
          // A dedication the book can't print (wrong alphabet / too long) must be fixed before we spend a generation.
          const dedicationBlocking = getDedicationIssue(form.getValues());
          if (dedicationBlocking?.blocking) {
            toast({ title: 'Dedication', description: dedicationBlocking.message, variant: 'destructive' });
            setIsNavigating(false);
            return;
          }
          // Check if story is already generated
          if (storyPreview) {
            // Story already generated, proceed to review
            wizard.goToNextStep();
          } else {
            // Generate story before moving to preview
            await generateStory();
            return; // Don't advance yet, generateStory will handle it
          }
        } else {
          wizard.goToNextStep();
        }
      } else {
        // Show specific validation message based on current step
        let errorMessage = "Please complete all required fields";
        
        switch (wizard.currentStep) {
          case 'story-type':
            errorMessage = "Please select whether you want a single or multi-character story";
            break;
          case 'photo-upload':
            if (wizard.storyType === 'multi') {
              const hasPhoto = form.getValues('characterPhoto');
              const faceCount = form.getValues('characterCount') || 0;
              if (!hasPhoto) {
                errorMessage = "Please upload a photo before continuing";
              } else if (faceCount === 0) {
                errorMessage = "Please select the faces in your photo or use manual selection";
              }
            } else {
              errorMessage = "Please upload a photo before continuing";
            }
            break;
          case 'character-details-review': {
            const name = form.getValues('childFirstName');
            const age = form.getValues('childAge');
            const gender = form.getValues('childGender');
            if (!name) errorMessage = "Please enter your child's name";
            else if (!age) errorMessage = "Please select your child's age";
            else if (!gender) errorMessage = "Please select your child's gender";
            break;
          }
          case 'appearance-review':
            errorMessage = "Please review the appearance settings";
            break;
          case 'story-selection':
            errorMessage = "Please select a story template for your book";
            break;
          case 'multi-story-selection':
            errorMessage = "Please select a story template for your multi-character adventure";
            break;
          case 'character-details': {
            const name = form.getValues('childFirstName');
            const age = form.getValues('childAge');
            const gender = form.getValues('childGender');
            if (!name) errorMessage = "Please enter the child's first name";
            else if (!age) errorMessage = "Please enter the child's age";
            else if (!gender) errorMessage = "Please select the child's gender";
            break;
          }
          case 'appearance': {
            const hairColor = form.getValues('hairColor');
            const skinColor = form.getValues('skinColor');
            if (!hairColor) errorMessage = "Please select a hair color";
            else if (!skinColor) errorMessage = "Please select a skin tone";
            break;
          }
          case 'style':
            errorMessage = "Please select an illustration style for your book";
            break;
          case 'story-creation':
            const bookTitle = form.getValues('bookTitle');
            const additionalComments = form.getValues('additionalComments');
            if (!bookTitle) errorMessage = "Please enter a title for your book";
            else if (!additionalComments) errorMessage = "Please describe what you'd like in the story";
            break;
        }
        
        toast({
          title: "Missing Information",
          description: errorMessage,
          variant: "destructive",
          duration: 4000,
        });
      }
    } finally {
      setIsNavigating(false);
    }
  };

  // Handle updating individual page content
  const handleDedicationChange = (value: string) => {
    form.setValue('dedication', value, { shouldDirty: true });
    setDedicationTick((t) => t + 1);
  };

  const handlePageContentUpdate = (pageNumber: number, newContent: string) => {
    if (!storyPreview) return;
    
    const updatedPages = storyPreview.map(page =>
      page.pageNumber === pageNumber
        ? { ...page, content: newContent }
        : page
    );

    setStoryPreview(updatedPages);

    // The cover page (page 0) content IS the book title. Editing it must also update the form's
    // bookTitle — that's the field shown in the H1 and sent to create-multi-character-async as the
    // book's title. Without this, editing the cover text changed only the page content and the title
    // appeared not to change at all.
    if (pageNumber === 0) {
      const newTitle = newContent.trim();
      if (newTitle) {
        form.setValue('bookTitle', newTitle, { shouldValidate: true, shouldDirty: true });
      }
    }

    toast({
      title: "Page Updated",
      description: `Page ${pageNumber === 0 ? 'cover' : pageNumber} content has been updated.`,
      duration: 2000,
    });
  };
  
  // Generate story function
  const generateStory = async () => {
    setIsLoading(true);
    setCurrentOperation('story');
    await getFreshCoverPhotoUrl();
    const data = form.getValues();
    track('story_requested', { variant, template: selectedTemplate?.id ?? 'custom' });
    
    // Request wake lock to prevent screen from sleeping during story generation
    await requestWakeLock('Story generation');
    
    try {
      let response;
      
      if (wizard.storyType === 'multi' && data.characters?.length) {
        // Multi-character story generation
        const faceDetectionResults = data.characters.reduce((acc, char) => {
          if (char.bbox) {
            acc[char.name] = char.bbox;
          }
          return acc;
        }, {} as Record<string, any>);

        // Debug: Log conflict/antagonist data being sent
        console.log('🎭 [FRONTEND] Sending storyContext with conflict data:', {
          hasTemplate: !!selectedTemplate,
          conflictType: selectedTemplate?.conflictType,
          antagonistName: selectedTemplate?.antagonist?.name,
          antagonistThreat: selectedTemplate?.antagonist?.threat
        });

        response = await fetchWithToast('/api/books/generate-multi-character-story-async', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionId && { 'x-session-id': sessionId })
          },
          body: JSON.stringify({
            characters: data.characters.map((char, index) => ({
              ...char,
              age: parseInt(char.age),
              isMainCharacter: index === data.mainCharacterIndex
            })),
            style: styles.find(s => s.id === data.styleId),
            bookTitle: data.bookTitle,
            additionalComments: data.additionalComments,
            pageCount: data.pageCount,
            readingLevel: data.readingLevel,
            storyContext: selectedTemplate ? {
              ...data.storyContext,
              ...selectedTemplate.storyContext,
              conflictType: selectedTemplate.conflictType,
              antagonist: selectedTemplate.antagonist,
              template: selectedTemplate  // Include the template for backend
            } : data.storyContext,
            language: data.language,
            faceDetectionResults: faceDetectionResults,
            photoUrl: data.characterPhoto,
            relationshipType: data.relationshipType  // Include relationship
          })
        }, toast);
      } else {
        // Single character story generation
        console.log('[WIZARD] Generating single character story with data:', {
          childDetails: {
            firstName: data.childFirstName || '',
            gender: data.childGender || Gender.OTHER,
            age: parseInt(data.childAge || '5'),
            hairColor: data.hairColor || 'brown',
            skinColor: data.skinColor || 'medium',
            language: data.language,
            photoUrl: data.characterPhoto
          },
          theme: selectedTemplate ? { 
            id: selectedTemplate.themeId, 
            name: selectedTemplate.category || 'Adventure', 
            description: selectedTemplate.description, 
            previewImageUrl: '', 
            isActive: true 
          } : null,
          style: styles.find(s => s.id === data.styleId),
          bookTitle: data.bookTitle,
          additionalComments: data.additionalComments,
          pageCount: data.pageCount
        });
        
        response = await fetchWithToast('/api/books/generate-story-async', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(sessionId && { 'x-session-id': sessionId })
          },
          body: JSON.stringify({
            ...photoConsentJson(photoConsent), // R3 (this route refuses a photo without it)
            quickMode: true, // ENABLE COVER-FIRST GENERATION! 🚀
            childDetails: {
              firstName: data.childFirstName || '',
              gender: data.childGender ? data.childGender.toUpperCase() : Gender.OTHER,
              age: parseInt(data.childAge || '5'),
              hairColor: data.hairColor || 'brown',
              skinColor: data.skinColor || 'medium',
              language: data.language,
              photoUrl: data.characterPhoto
            },
            theme: selectedTemplate ? { 
              id: selectedTemplate.themeId, 
              name: selectedTemplate.category || 'Adventure', 
              description: selectedTemplate.description, 
              previewImageUrl: '', 
              isActive: true 
            } : { 
              id: 'adventure', 
              name: 'Adventure', 
              description: 'An exciting journey', 
              previewImageUrl: '', 
              isActive: true 
            },
            style: styles.find(s => s.id === data.styleId),
            bookTitle: data.bookTitle,
            additionalComments: data.additionalComments,
            pageCount: data.pageCount,
            storyContext: selectedTemplate ? {
              ...data.storyContext,
              ...selectedTemplate.storyContext,
              conflictType: selectedTemplate.conflictType,
              antagonist: selectedTemplate.antagonist,
            } : data.storyContext,
          }),
        }, toast);
      }

      // Check if we got cover data (cover-first mode)
      if (response?.coverReady && response?.coverImageUrl) {
        // Show cover with actual image while story generates
        console.log('🎨 [COVER-FIRST] Cover image ready:', response.coverImageUrl);
        setCoverData({
          ...response.coverData,
          imageUrl: response.coverImageUrl // Add the actual image URL!
        });
        setCurrentOperation('cover');
        
        // Show the cover immediately
        toast({ 
          title: "✨ Cover Ready!", 
          description: "Your book cover is ready! Full story generating in background...",
          duration: 5000
        });
        
        // We still need to poll for the full story
        if (response?.requestId) {
          setStoryRequestId(response.requestId);
          console.log('[WIZARD] Cover-first mode: Request ID:', response.requestId);
          startPollingForStory(response.requestId);
        }
      } else if (response?.coverReady && response?.coverData) {
        // Fallback: Old behavior if no image URL
        console.log('🎨 [COVER-FIRST] Cover generated quickly (text only)!');
        setCoverData(response.coverData);
        setCurrentOperation('cover');
        
        // Show the cover immediately
        toast({ 
          title: "✨ Cover Ready!", 
          description: "Your book cover is ready! Full story generating in background...",
          duration: 5000
        });
        
        // We still need to poll for the full story
        if (response?.requestId) {
          setStoryRequestId(response.requestId);
          console.log('[WIZARD] Cover-first mode: Request ID:', response.requestId);
          startPollingForStory(response.requestId);
        }
      } else if (response?.requestId) {
        // Standard mode (no cover data)
        setStoryRequestId(response.requestId);
        console.log('[WIZARD] Standard mode: Request ID:', response.requestId);
        
        startPollingForStory(response.requestId);
        
        toast({ 
          title: "Story Generation Started", 
          description: `We're writing your personalized story. This usually takes ${STORY_TEXT_TIME}.`
        });
      } else {
        throw new Error(response?.error || "Could not start story generation.");
      }
    } catch (error) {
      console.error('[WIZARD] Error in generateStory:', error);
      setIsLoading(false);
      setCurrentOperation(null);
      
      // Release wake lock on error
      await releaseWakeLock();
      
      toast({ 
        title: "Error", 
        description: "Failed to generate story. Please try again.", 
        variant: "destructive" 
      });
    }
  };

  const handleBack = () => {
    console.log('[WIZARD] Back button clicked, current step:', wizard.currentStep);
    console.log('[WIZARD] Is loading:', isLoading);
    console.log('[WIZARD] Is first step:', wizard.isFirstStep);
    console.log('[WIZARD] Current form values:', form.getValues());
    console.log('[WIZARD] Story preview exists:', !!storyPreview);
    console.log('[WIZARD] Character outfits exist:', !!characterOutfits);
    wizard.goToPreviousStep();
  };

  // Render the appropriate step content (minimal logging)
  const renderStepContent = () => {
    // Pre-declare variables to avoid hoisting issues
    let relationshipType: string;
    let characters: any[];
    let selectedStyle: Style | undefined;
    
    // Get form values once to avoid multiple form.watch subscriptions during render
    const formValues = form.getValues();
    const dedicationError = getDedicationIssue(formValues)?.message ?? null;
    
    switch (wizard.currentStep) {
      case 'story-type':
        // MODIFICATION: Auto-advance to next step - user never sees this
        // The story type is already set to 'multi' in form defaults
        wizard.goToNextStep();
        return null;
      
      case 'photo-upload':
        // For single character, use simplified photo-only upload with auto-advance
        if (wizard.storyType === 'single') {
          const photoUrl = formValues.characterPhoto;
          
          return (
            <WizardStep
              title="Upload a Photo"
              description="Upload a clear photo of your child"
              helpText="We'll analyze the photo to help personalize your story."
            >
              <PhotoOnlyUploadStep
                photoUrl={photoUrl}
                onPhotoChange={(url) => {
                  form.setValue('characterPhoto', url);
                }}
                onUploadComplete={() => {
                  track('photo_uploaded');
                  toast({ title: "Success!", description: "Photo uploaded successfully! ✨" });
                }}
                onUploadError={(error) => {
                  toast({ title: "Upload Failed", description: error, variant: "destructive" });
                }}
                storyType="single"
                onAutoAdvance={() => {
                  wizard.goToNextStep();
                }}
                onAnalysisComplete={(analysis) => {
                  setAiAnalysis(analysis);
                }}
              />
            </WizardStep>
          );
        } else {
          // Multi-character keeps the original PhotoUploadStep with face detection
          const photoUrl = formValues.characterPhoto;
          const childFirstName = formValues.childFirstName;
          const childAge = formValues.childAge;
          
          return (
            <WizardStep
              title="Upload a Photo"
              description="Upload a group photo with all the characters"
              helpText="For best results, use a well-lit photo showing 1-5 people clearly. We'll help you select each person."
            >
              <PhotoUploadStep
                photoUrl={photoUrl}
                onPhotoChange={(url) => {
                  form.setValue('characterPhoto', url);
                }}
                onUploadComplete={() => {
                  track('photo_uploaded');
                  toast({ title: "Success!", description: "Photo uploaded successfully! ✨" });
                }}
                onUploadError={(error) => {
                  // Reason code only (the message can name the file); matches app/api/supabase-upload errors.
                  const e = String(error || '').toLowerCase();
                  track('photo_upload_failed', { reason: e.includes('too large') ? 'size' : e.includes('type') ? 'type' : 'error' });
                  toast({ title: "Upload Failed", description: error, variant: "destructive" });
                }}
                storyType="multi"
                form={form}
                onFacesDetected={(count) => {
                  setCharacterCount(count);
                  form.setValue('characterCount', count);
                }}
                childName={childFirstName}
                childAge={parseInt(childAge || '5')}
                onNameChange={(name) => form.setValue('childFirstName', name)}
                onAgeChange={(age) => form.setValue('childAge', age.toString())}
                onAnalysisComplete={(analysis) => {
                  setAiAnalysis(analysis);
                }}
                photoConsent={photoConsent}
                onPhotoConsentChange={setPhotoConsent}
                consentNotNeeded={photoConsentState.consentNotNeeded}
                coverSlug={photoConsentState.coverSlugForPhoto}
              />
            </WizardStep>
          );
        }
      
      case 'character-details-review':
        return (
          <WizardStep
            title="Tell Us About Your Child"
            description="Enter your child's details and review detected features"
          >
            <CharacterDetailsReviewStep
              photoUrl={formValues.characterPhoto}
              childName={formValues.childFirstName}
              childAge={formValues.childAge}
              childGender={formValues.childGender}
              aiAnalysis={stableAiAnalysisProp}
              onNameChange={(name) => form.setValue('childFirstName', name)}
              onAgeChange={(age) => form.setValue('childAge', age)}
              onGenderChange={(gender) => form.setValue('childGender', gender)}
              onAppearanceUpdate={(updates) => {
                if (updates.gender) form.setValue('childGender', updates.gender);
                if (updates.hairColor) form.setValue('hairColor', updates.hairColor);
                if (updates.skinTone) form.setValue('skinColor', updates.skinTone);
                if (updates.eyeColor) form.setValue('eyeColor', updates.eyeColor);
                if (updates.hairTexture) form.setValue('hairTexture', updates.hairTexture);
              }}
              errors={{
                name: form.formState.errors.childFirstName?.message,
                age: form.formState.errors.childAge?.message,
                gender: form.formState.errors.childGender?.message,
              }}
            />
          </WizardStep>
        );
      
      case 'appearance-review':
        // This step is no longer used for single character flow
        // but kept for backwards compatibility or potential future use
        return (
          <WizardStep
            title="Review Character Appearance"
            description="We've analyzed the photo! Tap to adjust any details"
          >
            <AppearanceReviewStep
              aiAnalysis={stableAiAnalysisProp}
              onUpdate={(updates) => {
                if (updates.gender) form.setValue('childGender', updates.gender);
                if (updates.hairColor) form.setValue('hairColor', updates.hairColor);
                if (updates.skinTone) form.setValue('skinColor', updates.skinTone);
              }}
              onContinue={() => {
                wizard.goToNextStep();
              }}
            />
          </WizardStep>
        );
      
      case 'story-selection':
        return (
          <WizardStep
            title=""
            description=""
          >
            <StorySelectionStep
              childDetails={{
                firstName: formValues.childFirstName || '',
                age: parseInt(formValues.childAge || '5'),
                gender: formValues.childGender as 'MALE' | 'FEMALE' | 'OTHER' | undefined
              }}
              photoUrl={formValues.characterPhoto}
              characters={characters}
              onSelect={(template, pageCount) => {
                setSelectedTemplate(template);
                form.setValue('pageCount', pageCount);
                // Get fresh form values inside the callback
                const currentFormValues = form.getValues();
                form.setValue('bookTitle', template.title.replace('[[NAME]]', currentFormValues.childFirstName || 'Child'));
                // Filter out multi-character sections for single-character stories
                const filteredComments = filterCommentsForCharacterCount(template.storyContext.additionalComments, 1);
                form.setValue('additionalComments', filteredComments);
                // Don't auto-advance - user needs to click Next
              }}
            />
          </WizardStep>
        );
      
      case 'character-details':
        return (
          <WizardStep
            title="Tell Us About Your Character"
            description="Help us personalize the story for your child"
          >
            <CharacterDetailsStep
              name={formValues.childFirstName}
              age={formValues.childAge}
              gender={formValues.childGender}
              onNameChange={(name) => form.setValue('childFirstName', name)}
              onAgeChange={(age) => form.setValue('childAge', age)}
              onGenderChange={(gender) => form.setValue('childGender', gender)}
              errors={{
                name: form.formState.errors.childFirstName?.message,
                age: form.formState.errors.childAge?.message,
                gender: form.formState.errors.childGender?.message,
              }}
            />
          </WizardStep>
        );
      
      case 'appearance':
        return (
          <WizardStep
            title="Customize Appearance"
            description="Select features to match your child"
          >
            <AppearanceStep
              hairColor={formValues.hairColor}
              skinColor={formValues.skinColor}
              onHairColorChange={(color) => form.setValue('hairColor', color)}
              onSkinColorChange={(color) => form.setValue('skinColor', color)}
              errors={{
                hairColor: form.formState.errors.hairColor?.message,
                skinColor: form.formState.errors.skinColor?.message,
              }}
            />
          </WizardStep>
        );
      
      case 'multi-character-details':
        const formCharacterCount = formValues.characterCount || 0;
        const formCharacters = formValues.characters || [];
        
        console.log('[WIZARD] Rendering multi-character-details:', {
          characterCount,
          formCharacterCount,
          formCharactersLength: formCharacters.length
        });
        
        // Use the form's character count if available, otherwise use state
        const effectiveCharacterCount = formCharacterCount || characterCount || 2;
        
        return (
          <WizardStep
            title="Tell Us About Each Character"
            description="Enter details for each person in your story"
            helpText="Click any character's tab to edit their details. You can choose who will be the main character of your story."
          >
            <MultiCharacterDetailsStep
              onNext={handleNext}
              onBack={handleBack}
              isFirstStep={wizard.isFirstStep}
              isLastStep={wizard.isLastStep}
              form={form}
              characterCount={effectiveCharacterCount}
              isAnalyzing={isAnalyzingMultiChar}
            />
            {variant === 'fast' && !stepSequence.includes('multi-character-appearance') && (
              <div className="mt-6">
                <AdjustLooksDrawer
                  form={form}
                  characterCount={effectiveCharacterCount}
                  onOpen={() => {
                    if (drawerOpenedTracked.current) return;
                    drawerOpenedTracked.current = true;
                    track('appearance_adjusted', { variant, source: 'drawer_opened' });
                  }}
                  onAdjust={() => track('appearance_adjusted', { variant, source: 'value_changed' })}
                />
              </div>
            )}
          </WizardStep>
        );
      
      case 'multi-character-appearance':
        return (
          <WizardStep
            title="Customize Each Character"
            description="Select appearance features for each character"
            helpText="Click on each character's tab to customize their appearance."
          >
            <MultiCharacterAppearanceStep
              onNext={handleNext}
              onBack={handleBack}
              isFirstStep={wizard.isFirstStep}
              isLastStep={wizard.isLastStep}
              form={form}
              characterCount={characterCount || 2}
            />
          </WizardStep>
        );
      
      case 'multi-story-selection': {
        relationshipType = formValues.relationshipType || 'siblings';
        characters = formValues.characters || [];
        
        return (
          <WizardStep
            title=""
            description=""
          >
            <MultiCharacterStorySelectionStep
              characters={characters.map((char, idx) => ({
                ...char,
                isMainCharacter: idx === formValues.mainCharacterIndex
              }))}
              relationshipType={relationshipType}
              photoUrl={formValues.characterPhoto}
              onSelect={(template, pageCount) => {
                setSelectedTemplate(template);
                form.setValue('pageCount', pageCount);

                // Personalize the title with all character names
                const personalizedTitle = personalizeTitle(
                  template as any,
                  characters.map((char) => ({
                    name: char.name
                  }))
                );

                form.setValue('bookTitle', personalizedTitle);
                // Filter comments based on character count (keeps multi-char sections for 2+ characters)
                const filteredComments = filterCommentsForCharacterCount(template.storyContext.additionalComments, characters.length);
                form.setValue('additionalComments', filteredComments);
                // Don't auto-advance - user needs to click Next
              }}
            />
          </WizardStep>
        );
      }
      
      case 'style':
        console.log('[WIZARD] Rendering style step with:', {
          styles: styles,
          selectedStyleId: formValues.styleId
        });
        return (
          <WizardStep
            title="Choose an Art Style"
            description="Select the illustration style for your book"
          >
            <StyleSelectionStep
              styles={styles}
              selectedStyleId={formValues.styleId}
              onStyleSelect={(styleId) => {
                form.setValue('styleId', styleId);
                // Don't auto-advance - user needs to click Next
              }}
              error={form.formState.errors.styleId?.message}
            />
          </WizardStep>
        );
      
      case 'story-creation':
        return (
          <WizardStep
            title="Finalize Your Story"
            description="Review and adjust the story details"
          >
            {fromCoverReuse && fromCoverSlug && (
              <div className="mb-6 flex items-center gap-4 rounded-lg border border-green-200 bg-green-50 p-4">
                <img src={`/c/${fromCoverSlug}/image`} alt="Your free cover" className="h-24 w-16 rounded object-cover" />
                <p className="text-sm text-green-800">
                  <strong>Your free cover becomes the cover of this book.</strong> We&apos;ll write a story to match it.
                </p>
              </div>
            )}
            {!fromCoverReuse && coverNotReusedReason && (
              <p className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{coverNotReusedReason}</p>
            )}
            {fromCoverReuse && !stepSequence.includes('multi-character-details') && (
              <div className="mb-6">
                <AdjustLooksDrawer
                  form={form}
                  includeDetails
                  characterCount={formValues.characterCount || characterCount || 1}
                  onOpen={() => {
                    if (drawerOpenedTracked.current) return;
                    drawerOpenedTracked.current = true;
                    track('appearance_adjusted', { variant, source: 'drawer_opened' });
                  }}
                  onAdjust={() => track('appearance_adjusted', { variant, source: 'value_changed' })}
                />
              </div>
            )}
            <StoryCreationStep
              language={formValues.language}
              bookTitle={formValues.bookTitle}
              additionalComments={formValues.additionalComments}
              pageCount={formValues.pageCount}
              culturalSetting={formValues.storyContext?.culturalSetting}
              culturalDetails={formValues.storyContext?.culturalDetails}
              onLanguageChange={(lang) => {
                console.log('🌍 [LANGUAGE-CHANGE] Language changed to:', lang);
                form.setValue('language', lang, {
                  shouldValidate: true,
                  shouldDirty: true,
                  shouldTouch: true
                });
                console.log('🌍 [LANGUAGE-CHANGE] Form language value after set:', form.getValues('language'));
              }}
              onTitleChange={(title) => form.setValue('bookTitle', title)}
              onCommentsChange={(comments) => form.setValue('additionalComments', comments)}
              dedication={formValues.dedication}
              onDedicationChange={handleDedicationChange}
              dedicationError={dedicationError}
              onPageCountChange={(count) => form.setValue('pageCount', count)}
              readingLevel={formValues.readingLevel ?? 'picture'}
              onReadingLevelChange={(l) => form.setValue('readingLevel', l)}
              onCulturalSettingChange={(setting) => form.setValue('storyContext.culturalSetting', setting)}
              onCulturalDetailsChange={(details) => form.setValue('storyContext.culturalDetails', details)}
              isMultiCharacter={wizard.storyType === 'multi'}
              occasionFirst={variant === 'fast' && !fromCoverReuse}
              onTemplateSelect={setSelectedTemplate}
              initialTemplateId={fromCoverReuse ? coverStoryTemplateId : (selectedTemplate?.id ?? null)}
              heldTemplateId={selectedTemplate?.id ?? null}
              characterName={formValues.childFirstName}
              characters={formValues.characters?.map(c => ({ name: c.name, isMainCharacter: c.name === formValues.characters?.[formValues.mainCharacterIndex || 0]?.name }))}
              errors={{
                language: form.formState.errors.language?.message,
                bookTitle: form.formState.errors.bookTitle?.message,
                additionalComments: form.formState.errors.additionalComments?.message,
              }}
            />
          </WizardStep>
        );
      
      case 'story-preview':
        selectedStyle = styles.find(s => s.id === formValues.styleId);
        return (
          <WizardStep
            title="Review Your Story"
            description="Preview your story before creating the book"
          >
            {storyPreview && selectedStyle ? (
              <StoryPreviewStep
                storyPages={storyPreview}
                bookTitle={formValues.bookTitle}
                styleName={selectedStyle.name}
                pageCount={formValues.pageCount}
                isMultiCharacter={wizard.storyType === 'multi'}
                onPageContentChange={handlePageContentUpdate}
                dedication={formValues.dedication}
                onDedicationChange={handleDedicationChange}
                dedicationError={dedicationError}
                readingLevel={formValues.readingLevel ?? 'picture'}
                language={formValues.language || 'en'}
              />
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>Generating your story...</p>
              </div>
            )}
          </WizardStep>
        );
      
      default:
        return null;
    }
  };
  
  // CRITICAL FIX #6: Prevent rendering during rapid transitions (simplified)
  if (isTransitioningRef.current && wizard.currentStep === 'appearance-review') {
    return (
      <WizardErrorBoundary>
        <WizardContainer
          currentStep={wizard.currentStep}
          storyType={wizard.storyType}
          onStepChange={(step) => wizard.goToStep(step)}
        >
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-r-transparent"></div>
          </div>
        </WizardContainer>
      </WizardErrorBoundary>
    );
  }
  
  return (
    <WizardErrorBoundary>
      <WizardContainer
        currentStep={wizard.currentStep}
        storyType={wizard.storyType}
        onStepChange={(step) => wizard.goToStep(step)}
      >
        {/* Hidden debug state */}
        <div 
          data-wizard-state={JSON.stringify({
            currentStep: wizard.currentStep,
            storyType: wizard.storyType,
            completedSteps: Array.from(wizard.completedSteps),
            isLoading,
            currentOperation,
            hasStoryPreview: !!storyPreview,
            formValues: form.getValues()
          })} 
          style={{ display: 'none' }} 
        />
        
        {/* Progress indicator */}
        <div className="px-4 pt-4 sm:px-6 sm:pt-6 flex-shrink-0" data-step-indicator="true">
          {/* Free Preview Banner - Only show on first few steps */}
          {(wizard.currentStep === 'story-type' || wizard.currentStep === 'photo-upload' || wizard.currentStep === 'character-details-review' || wizard.currentStep === 'story-selection' || wizard.currentStep === 'multi-story-selection') && (
            <div className="bg-blue-50 border border-blue-200 p-4 mb-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Try it free!</strong> Create and preview your book cover with no credit card required.
                    You only pay if you decide to purchase the complete book.
                  </p>
                </div>
              </div>
            </div>
          )}
          {/* Language Tip Banner - Show on earlier steps to inform about language selection */}
          {(wizard.currentStep === 'photo-upload' || wizard.currentStep === 'multi-character-details' || wizard.currentStep === 'multi-character-appearance') && (
            <div className="bg-amber-50 border border-amber-200 p-4 mb-4 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    <strong>Creating a non-English book?</strong> Answer questions in English for now.
                    On the "Finalize Your Story" page, you'll select your language and everything will be automatically translated!
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <StepIndicator
              currentStep={wizard.currentStep}
              completedSteps={wizard.completedSteps}
              storyType={wizard.storyType}
              steps={stepSequence}
              onStepClick={wizard.completedSteps.size > 0 ? wizard.goToStep : undefined}
            />
            <KeepScreenOnBadge className="ml-4" />
          </div>
        </div>
        
        {/* Step content wrapper without animation (temporary fix for React Error #310) */}
        <div className="flex-1 overflow-hidden" data-step-content="true">
          <div className="h-full">
            <div className="h-full overflow-y-auto p-4" {...wizard.touchHandlers}>
              {renderStepContent()}
            </div>
          </div>
        </div>
        
        {/* Navigation - fixed at bottom on mobile */}
        {wizard.currentStep !== 'story-type' && (
          <CardFooter className="border-t mt-auto p-0 sm:p-6 flex-shrink-0">
            {/* Payment reassurance for final step */}
            {wizard.isLastStep && (
              <div className="w-full mb-4 px-4 sm:px-0">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 rounded-full p-1">
                      <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-800 mb-1">100% Free Preview</h4>
                      <p className="text-sm text-green-700">
                        No payment or credit card required! See your personalized book cover first, 
                        then decide if you'd like to purchase the complete story.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Navigation - fixed at bottom on mobile */}
            <MobileWizardNavigation
                onNext={handleNext}
                onBack={handleBack}
                onSubmit={handleCreateBookPreview}
                isFirstStep={wizard.isFirstStep}
                isLastStep={wizard.isLastStep}
                isValid={isStepValid}
                nextLabel={nextLabel}
                submitLabel="Create Book Cover Preview (Free)"
                className="w-full"
                isLoading={isLoading || isNavigating}
              />
          </CardFooter>
        )}
      </WizardContainer>
      
      {/* Loading overlay */}
      <LoadingOverlay
        operation={currentOperation}
        childName={loadingOverlayProps.childName}
        isMultiCharacter={wizard.storyType === 'multi'}
        characterCount={loadingOverlayProps.characterCount}
        characterNames={loadingOverlayProps.characterNames}
        mainCharacterName={loadingOverlayProps.mainCharacterName}
        coverData={coverData} // Pass cover data to show preview
      />
      
      {/* Progress tracker - shows in bottom right when generating */}
      <GenerationProgressTracker
        operation={currentOperation}
        isVisible={isLoading && currentOperation !== null}
        characterName={loadingOverlayProps.childName || loadingOverlayProps.mainCharacterName}
        isMultiCharacter={wizard.storyType === 'multi'}
      />

      {/* Cover-notify escape hatch: shown after 15s during preview/cover generation */}
      {showCoverNotify && !coverNotifyDismissed && previewRequestId && sessionId && (
        <div className="fixed bottom-6 left-0 right-0 flex justify-center px-4 z-[60]">
          <CoverNotifyCard
            bookId={previewRequestId}
            sessionId={sessionId}
            onDismiss={() => setCoverNotifyDismissed(true)}
          />
        </div>
      )}

      {/* Floating Trust Badge - Desktop only */}
      <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-full px-4 py-2 z-40 hidden md:flex items-center gap-2 border border-green-200">
        <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <span className="text-sm font-medium">Free Preview</span>
      </div>
    </WizardErrorBoundary>
  );
}
